import React, { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Button,
} from "@mui/material";
import Login from "./components/Login";
import Navbar from "./components/Navbar";
import {
  changeOwnPassword,
  clearCurrentUserContext,
  getActiveSchoolId,
  getSession,
  logout,
  setActiveSchoolId,
  setCurrentUserContext,
} from "./services/dataService";

const Dashboard = lazy(() => import("./components/Dashboard"));
const StudentManagement = lazy(() => import("./components/StudentManagement"));
const ClassRecord = lazy(() => import("./components/ClassRecord"));
const CourseManagement = lazy(() => import("./components/CourseManagement"));
const SchoolManagement = lazy(() => import("./components/SchoolManagement"));
const StatisticsData = lazy(() => import("./components/StatisticsData"));
const UserManagement = lazy(() => import("./components/UserManagement"));

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = async () => {
      try {
        const sessionUser = await getSession();
        if (!isMounted) {
          return;
        }
        setCurrentUserContext(sessionUser);
        if (sessionUser?.schoolId) {
          const nextSchoolId =
            sessionUser.role === "admin"
              ? getActiveSchoolId() || sessionUser.schoolId
              : sessionUser.schoolId;
          setActiveSchoolId(nextSchoolId);
        }
        setUser(sessionUser);
        setIsAuthenticated(true);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        clearCurrentUserContext();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        if (isMounted) {
          setAuthChecking(false);
        }
      }
    };

    bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogin = (userData) => {
    setCurrentUserContext(userData);
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Failed to logout", error);
    }
    clearCurrentUserContext();
    setIsAuthenticated(false);
    setUser(null);
  };

  if (authChecking) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const defaultPath = user?.role === "mentor" ? "/class-record" : "/dashboard";
  const canAccessAdminPages = user?.role === "admin";
  const forcePasswordChange =
    user?.loginType === "local" && user?.mustChangePassword;

  const handlePasswordChange = async () => {
    setPasswordError("");
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setPasswordError("現在のパスワードと新しいパスワードを入力してください。");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError("新しいパスワードは8文字以上で入力してください。");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("新しいパスワード（確認）が一致しません。");
      return;
    }

    try {
      setPasswordSaving(true);
      const updatedUser = await changeOwnPassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        schoolId: user?.schoolId,
      });
      const nextUser = {
        ...user,
        ...updatedUser,
        loginType: "local",
      };
      setCurrentUserContext(nextUser);
      setUser(nextUser);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      const status = error?.response?.status;
      const apiError = String(error?.response?.data?.error || "").trim();
      if (status === 401 && apiError === "authentication required") {
        setPasswordError(
          "セッションの有効期限が切れました。再度ログインしてください。"
        );
      } else if (status === 401) {
        setPasswordError("現在のパスワードが正しくありません。");
      } else {
        setPasswordError("パスワード変更に失敗しました。");
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Swimmy Tool
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.name}さん
          </Typography>
        </Toolbar>
      </AppBar>

      <Navbar onLogout={handleLogout} user={user} />

      <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
        <Suspense fallback={<LinearProgress sx={{ mt: 2 }} />}>
          <Routes>
            <Route path="/" element={<Navigate to={defaultPath} replace />} />
            <Route
              path="/dashboard"
              element={
                canAccessAdminPages ? (
                  <Dashboard />
                ) : (
                  <Navigate to={defaultPath} replace />
                )
              }
            />
            <Route path="/students" element={<StudentManagement />} />
            <Route path="/class-record" element={<ClassRecord />} />
            <Route path="/class-record/:studentId" element={<ClassRecord />} />
            <Route path="/statistics-data" element={<StatisticsData />} />
            <Route
              path="/statistics-data/:studentId"
              element={<StatisticsData />}
            />
            <Route path="/mentors" element={<Navigate to="/accounts" replace />} />
            <Route
              path="/courses"
              element={
                canAccessAdminPages ? (
                  <CourseManagement />
                ) : (
                  <Navigate to={defaultPath} replace />
                )
              }
            />
            <Route
              path="/schools"
              element={
                canAccessAdminPages ? (
                  <SchoolManagement />
                ) : (
                  <Navigate to={defaultPath} replace />
                )
              }
            />
            <Route
              path="/accounts"
              element={
                canAccessAdminPages ? (
                  <UserManagement />
                ) : (
                  <Navigate to={defaultPath} replace />
                )
              }
            />
            <Route path="*" element={<Navigate to={defaultPath} replace />} />
          </Routes>
        </Suspense>
      </Container>

      <Dialog open={forcePasswordChange} disableEscapeKeyDown>
        <DialogTitle>初回パスワード変更</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            セキュリティのため、初回ログイン時にパスワード変更が必要です。
          </Typography>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label="現在のパスワード"
            value={passwordForm.currentPassword}
            onChange={(e) =>
              setPasswordForm((prev) => ({
                ...prev,
                currentPassword: e.target.value,
              }))
            }
          />
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label="新しいパスワード"
            value={passwordForm.newPassword}
            onChange={(e) =>
              setPasswordForm((prev) => ({
                ...prev,
                newPassword: e.target.value,
              }))
            }
          />
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label="新しいパスワード（確認）"
            value={passwordForm.confirmPassword}
            onChange={(e) =>
              setPasswordForm((prev) => ({
                ...prev,
                confirmPassword: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handlePasswordChange}
            disabled={passwordSaving}
          >
            {passwordSaving ? "変更中..." : "パスワードを更新"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default App;
